// How a fight is staged on the painting: fighters drawn apart so each reads alone, their tags clear of every
// figure, and a held beat after the player's own death. Presentation only: the sim's positions never change.

/** Painted-cell pixels, times the figure's drawn scale: a body's width plus a gap, and a figure's height. */
const SPACING = 46, HEIGHT = 60, BODY = 13;
const offsets = new Map<string, number>();
/** Every life's player is person:pc: a life that ends drops its fighters' offsets, so the next one is not drawn shifted. */
export const resetStaging = (): void => offsets.clear();

/** Fighters who would overlap on screen (close side by side, or one standing just behind another) are drawn apart
 * sideways, never up or down the floor, since depth does not separate two figures on a painting. The offset eases in
 * and out, and a body keeps the offset it fell with. */
export function spaceFighters(actors: { id: string; x: number; dead: boolean; y: number }[], fighting: ReadonlySet<string>,
  scale: (id: string) => number, delta: number): void {
  const wanted = new Map<string, number>();
  const fighters = actors.filter(actor => fighting.has(actor.id) && !actor.dead);
  for (const [i, a] of fighters.entries()) for (const b of fighters.slice(i + 1)) {
    const s = Math.max(scale(a.id), scale(b.id)), dx = b.x - a.x;
    if (Math.abs(dx) >= SPACING * s || Math.abs(b.y - a.y) >= HEIGHT * .6 * s) continue;
    const side = dx > 0 || (dx === 0 && a.id < b.id) ? 1 : -1, need = (SPACING * s - Math.abs(dx)) / 2;
    wanted.set(a.id, (wanted.get(a.id) ?? 0) - side * need); wanted.set(b.id, (wanted.get(b.id) ?? 0) + side * need);
  }
  const rate = 1 - Math.exp(-delta / 90);
  for (const actor of actors) {
    const current = offsets.get(actor.id) ?? 0;
    // A saved fight may open paused. Establish its spacing before holding it.
    const next = actor.dead ? current : !offsets.has(actor.id) && delta === 0 ? wanted.get(actor.id) ?? 0
      : current + ((wanted.get(actor.id) ?? 0) - current) * rate;
    if (Math.abs(next) < .05 && !wanted.has(actor.id)) { offsets.delete(actor.id); continue; }
    offsets.set(actor.id, next); actor.x += next;
  }
}

export interface Tag { node: HTMLElement; x: number; bottom: number }
/** Screen pixels. Tags stand in one row per cluster, spread sideways so none overlaps another, and each rises above
 * the head of every figure beneath it, so no tag ever covers a body. */
export function placeTags(tags: Tag[], figures: readonly { x: number; foot: number; scale: number }[], zoom: number, right = Infinity,
  column?: { left: number; bottom: number }): void {
  const placed = tags.map(tag => ({ ...tag, width: tag.node.offsetWidth, height: tag.node.offsetHeight })).sort((a, b) => a.x - b.x);
  // ponytail: clusters re-centre once; two clusters spread into each other only with many fighters in a line.
  let start = 0;
  for (let i = 0; i <= placed.length; i++) {
    const tag = placed[i], previous = placed[i - 1];
    if (tag && previous && tag.x < previous.x + (previous.width + tag.width) / 2 + 6) { tag.x = previous.x + (previous.width + tag.width) / 2 + 6; continue; }
    const cluster = placed.slice(start, i), shift = cluster.reduce((sum, t) => sum + t.x - tags.find(o => o.node === t.node)!.x, 0) / (cluster.length || 1);
    for (const t of cluster) t.x -= shift;
    start = i;
  }
  // Right to left: every tag stays on screen, out of the side column only where the column reaches down to it, and
  // clear of the tag on its right. A tag pushed off its fighter here would name the wrong figure, so nothing else moves it.
  let limit = right;
  for (const tag of [...placed].reverse()) {
    const edge = column && tag.bottom - tag.height < column.bottom ? Math.min(limit, column.left) : limit;
    if (tag.x + tag.width / 2 > edge) tag.x = edge - tag.width / 2;
    limit = tag.x - tag.width / 2 - 6;
  }
  for (const tag of placed) {
    let bottom = tag.bottom;
    for (const figure of figures) {
      if (Math.abs(figure.x - tag.x) < tag.width / 2 + BODY * figure.scale * zoom) bottom = Math.min(bottom, figure.foot - HEIGHT * figure.scale * zoom - 4);
    }
    tag.node.style.left = `${tag.x}px`; tag.node.style.top = `${bottom}px`;
  }
}

/** The player's own death: the fall, a held moment and the scene dimming, then `done` (the death card). Any click or
 * key ends the beat early. Returns a cancel for a host torn down mid-beat. */
export function deathBeat(done: () => void, ms = 2800): () => void {
  let over = false;
  const finish = (): void => { if (over) return; cancel(); done(); };
  const cancel = (): void => {
    over = true; window.clearTimeout(timer);
    window.removeEventListener('pointerdown', finish, true); window.removeEventListener('keydown', finish, true);
  };
  const timer = window.setTimeout(finish, ms);
  window.addEventListener('pointerdown', finish, true); window.addEventListener('keydown', finish, true);
  return cancel;
}
